/**
 * How an account comes to have a device list, and how it grows.
 *
 * There is no migration here and that is the point. An earlier draft had a
 * step where a person confirmed the devices the server already claimed —
 * unavoidable if you are retrofitting onto live accounts, and the one moment
 * in the whole design that cryptography could not make safe, because the first
 * list would inherit whatever the server said. Starting from nothing removes
 * it: the master key is created at the same instant as the first device, by
 * the same client, so the list is authoritative from birth and no human ever
 * has to adjudicate a set of devices they cannot check.
 *
 * After that there is exactly one way in. A new device publishes its bundle
 * and is UNAUTHORISED — visible, usable by nobody, in no list. The device
 * holding the master shows it, with its fingerprint, and a person compares
 * that fingerprint against the screen of the device in their other hand. That
 * comparison is the security of the whole mechanism: a server that substitutes
 * a device of its own into the request produces a fingerprint that does not
 * match what the new phone is showing. It is the same thing Signal's linking
 * QR does, read aloud instead of scanned.
 */

import { nextList, signList, listHash, verifyList, verifyChain, LIST_ERROR } from './deviceList.mjs';
import { masterKeyPair, masterPublicKey, hasMaster } from './masterIdentity.mjs';

/**
 * What the master-holder last signed: `{version, hash}`, kept next to the
 * master itself.
 *
 * Without it the holder took `previous` from the server and signed whatever
 * came next — so a server replaying an older valid list got a master
 * signature over a fork built on stale membership (audit 01.09 P1-02). The
 * holder now signs only from the exact list it last signed, or from a chain
 * of signed versions that provably leads there from it.
 */
const pinKey = (accountId) => `voxiox_master_list:${accountId}`;
export function pinnedListState(storage, accountId) {
  try {
    const raw = storage.getItem(pinKey(accountId));
    if (!raw) return null;
    const v = JSON.parse(raw);
    return Number.isInteger(v.version) && typeof v.hash === 'string' ? v : null;
  } catch (e) { return null; }
}
export function pinListState(storage, accountId, list) {
  storage.setItem(pinKey(accountId), JSON.stringify({ version: list.version, hash: listHash(list) }));
}

/**
 * The published list, admitted only if it is the one this holder last
 * signed — or continues from it by a chain the holder can walk. Returns
 * `{ ok, list, sig, reason }`. No pin yet (a holder from before pins
 * existed) admits the published list once and pins it.
 */
async function publishedForHolder({ api, storage, accountId }) {
  const pinned = pinnedListState(storage, accountId);
  const published = await api.getDeviceList(accountId, pinned ? pinned.version : null);
  if (!published || !published.published) return { ok: true, list: null, sig: null, pinned };
  const master = masterPublicKey(storage, accountId);
  if (published.masterKey && published.masterKey !== master) {
    return { ok: false, reason: 'опубликованный список подписан другим мастер-ключом' };
  }
  if (!pinned) {
    const verdict = verifyList({ list: published.list, sig: published.sig }, master);
    if (!verdict.ok) return { ok: false, reason: `опубликованный список не проверяется: ${verdict.error}` };
    return { ok: true, list: published.list, sig: published.sig, pinned: null };
  }
  const seen = { version: pinned.version, hash: pinned.hash };
  let verdict = verifyList({ list: published.list, sig: published.sig }, master, seen);
  if (!verdict.ok && verdict.error === LIST_ERROR.GAP) {
    const walked = verifyChain(published.chain, master, seen);
    verdict = walked.ok && walked.hash === listHash(published.list) ? walked
      : (walked.ok ? { ok: false, error: LIST_ERROR.GAP, seen } : walked);
  }
  if (!verdict.ok) {
    const why = verdict.error === LIST_ERROR.ROLLBACK ? 'сервер отдал список старее того, что подписывался последним'
      : verdict.error === LIST_ERROR.FORKED ? 'опубликованный список не продолжает тот, что подписывался последним'
      : verdict.error === LIST_ERROR.GAP ? 'между подписанным и опубликованным списком нет проверяемой цепочки'
      : `опубликованный список не проверяется: ${verdict.error}`;
    return { ok: false, reason: why };
  }
  return { ok: true, list: published.list, sig: published.sig, pinned };
}

/** Who came and who went between two lists — shown to the person who signs. */
export function listDelta(previous, next) {
  const before = new Set((previous ? previous.devices : []).map(d => d.deviceId));
  const after = new Set(next.devices.map(d => d.deviceId));
  return {
    added: next.devices.filter(d => !before.has(d.deviceId)).map(d => d.deviceId),
    removed: (previous ? previous.devices : []).filter(d => !after.has(d.deviceId)).map(d => d.deviceId),
  };
}

export const ACCOUNT_STATE = {
  FRESH: 'fresh',               // no list at all: this device makes one
  AUTHORISED: 'authorised',     // a verified list covers this device
  WAITING: 'waiting',           // a list exists; this device is not in it yet
  FOREIGN: 'foreign',           // a list exists signed by a master we cannot verify
};

/**
 * Where this device stands with its own account.
 *
 * `pinnedMaster` is what we believe the account's master key to be, from our
 * own storage or from the pin. Passing it separately keeps this function from
 * deciding trust: it reports, the caller acts.
 */
export async function accountState({ api, storage, accountId, deviceId, pinnedMaster = null }) {
  const published = await api.getDeviceList(accountId);
  const held = masterPublicKey(storage, accountId);
  const expected = held || pinnedMaster;

  if (!published || !published.published) {
    return { state: ACCOUNT_STATE.FRESH, canCreate: true };
  }
  // Holding no master is the ordinary condition of every device except one.
  // It used to land here as FOREIGN, which is the word for "somebody else's
  // account" — so a second phone, correctly waiting to be let in, reported
  // itself as an impostor and had nowhere to go. Not knowing the master and
  // knowing a DIFFERENT one are opposite facts and must not share an answer.
  //
  // With no pin this is first sight of our own account's key, and the caller
  // is expected to have pinned it exactly as it pins anybody else's: one
  // mechanism, so that a master which later CHANGES means the same thing here
  // as it does for a contact.
  if (expected && published.masterKey !== expected) {
    return { state: ACCOUNT_STATE.FOREIGN, published, masterChanged: true };
  }
  const master = expected || published.masterKey;
  const verdict = verifyList({ list: published.list, sig: published.sig }, master);
  if (!verdict.ok) return { state: ACCOUNT_STATE.FOREIGN, published, error: verdict.error };

  const listed = published.list.devices.some(d => d.deviceId === deviceId);
  return {
    state: listed ? ACCOUNT_STATE.AUTHORISED : ACCOUNT_STATE.WAITING,
    list: published.list, version: verdict.version, hash: verdict.hash,
    holdsMaster: hasMaster(storage, accountId),
  };
}

/**
 * The first device of an account: mint the master, sign a list of one.
 *
 * Refuses if a list already exists. Two masters for one account is the state
 * where half the peers pin one and half the other, and no screen anywhere
 * would explain it.
 */
export async function createAccountList({ api, storage, accountId, device, now = 0 }) {
  const published = await api.getDeviceList(accountId);
  if (published && published.published) {
    return { ok: false, reason: 'у аккаунта уже есть список устройств' };
  }
  const master = masterKeyPair(storage, accountId, { create: true });
  const list = nextList(null, { account: accountId, devices: [device], issuedAt: now });
  const signed = signList(master.secretKey, list);
  await api.putDeviceList({
    list, sig: signed.sig, masterKey: masterPublicKey(storage, accountId), listHash: listHash(list),
  });
  pinListState(storage, accountId, list);
  return { ok: true, version: 1, list, masterKey: masterPublicKey(storage, accountId) };
}

/**
 * Devices of ours that have published a bundle and are in no list.
 *
 * Not a separate table and not a protocol: a device that is not authorised IS
 * the request. The server carries it, which is all the server is for here —
 * the fingerprint the person compares is what makes the carrying safe.
 */
export async function pendingDevices({ api, storage, accountId }) {
  const state = await accountState({ api, storage, accountId, deviceId: null });
  if (state.state !== ACCOUNT_STATE.AUTHORISED && state.state !== ACCOUNT_STATE.WAITING) {
    return { pending: [], list: null, state: state.state };
  }
  const { devices } = await api.listDevices(accountId);
  const listed = new Set(state.list.devices.map(d => d.deviceId));
  return {
    state: state.state,
    list: state.list,
    pending: (devices || []).filter(d => !listed.has(d.deviceId)).map(d => ({
      deviceId: d.deviceId, identitySig: d.identitySig, identityDh: d.identityDh,
      label: d.deviceLabel || null, lastSeen: d.lastSeen || null,
    })),
  };
}

/**
 * Authorise another device. Only possible where the master lives, which is
 * exactly the step a hostile server cannot take.
 *
 * The caller is expected to have shown the person this device's fingerprint
 * and had them compare it with the device itself. Nothing here can check that
 * they did — it is the one part of the system that lives outside it.
 */
export async function authoriseDevice({ api, storage, accountId, device, now = 0 }) {
  const master = masterKeyPair(storage, accountId, { create: false });
  if (!master) return { ok: false, reason: 'мастер-ключ аккаунта не на этом устройстве' };

  const base = await publishedForHolder({ api, storage, accountId });
  if (!base.ok) return { ok: false, reason: base.reason };
  const previous = base.list;
  const list = nextList(previous, { account: accountId, add: device, issuedAt: now });
  const signed = signList(master.secretKey, list);
  await api.putDeviceList({
    list, sig: signed.sig, masterKey: masterPublicKey(storage, accountId), listHash: listHash(list),
  });
  pinListState(storage, accountId, list);
  return { ok: true, version: list.version, list, delta: listDelta(previous, list) };
}

/**
 * Retire a device: a new version without it.
 *
 * Never the last one. An empty list authorises nothing, which would lock the
 * account out of itself with no way back that does not involve the server —
 * and the server is the thing this list exists to stop believing.
 */
export async function revokeDevice({ api, storage, accountId, deviceId, now = 0 }) {
  const master = masterKeyPair(storage, accountId, { create: false });
  if (!master) return { ok: false, reason: 'мастер-ключ аккаунта не на этом устройстве' };
  const base = await publishedForHolder({ api, storage, accountId });
  if (!base.ok) return { ok: false, reason: base.reason };
  const previous = base.list;
  if (!previous) return { ok: false, reason: 'списка устройств ещё нет' };
  if (previous.devices.length <= 1) {
    return { ok: false, reason: 'нельзя убрать последнее устройство — аккаунт останется без единого' };
  }

  const list = nextList(previous, { account: accountId, removeDeviceId: deviceId, issuedAt: now });
  const signed = signList(master.secretKey, list);
  await api.putDeviceList({
    list, sig: signed.sig, masterKey: masterPublicKey(storage, accountId), listHash: listHash(list),
  });
  pinListState(storage, accountId, list);
  return { ok: true, version: list.version, list, delta: listDelta(previous, list) };
}

/**
 * Eight characters a person reads off one screen and compares with another.
 *
 * Not a safety number and not a substitute for one: it identifies a device
 * being linked, at the one moment when the two devices are in the same pair of
 * hands. Derived from the device id, which is itself a hash of both identity
 * keys, so it cannot be made to match a different device's keys.
 */
export function linkFingerprint(deviceId) {
  if (!deviceId) return '';
  const clean = String(deviceId).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return `${clean.slice(0, 4)} ${clean.slice(4, 8)}`;
}
